import { useQuery } from '@tanstack/react-query'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { getPollingGCTime } from '@omnea-app/frontend/utils/react-query/queryConfig'
import { TasksTopLevelFilterKey } from '@omnea-app/tasks/tasksFrontendClient'
import { Task, TasksHttpClient } from '@omnea-app/tasks/tasksHttpClient'
import { taskQueryKeys } from './queryKeys'

const ONE_MINUTE = 60_000

const tasksPerBatch = 100

const fetchTasksBatch = async (apiClient: TasksHttpClient, page: number, topLevelFilterKey: TasksTopLevelFilterKey | null) => {
  const { data } = await apiClient.POST('/', {
    body: { paginationModel: { page, pageSize: tasksPerBatch }, topLevelFilter: topLevelFilterKey ?? undefined },
  })
  return data
}

interface Props {
  topLevelFilterKey: TasksTopLevelFilterKey | null
}

export const useTasks = ({ topLevelFilterKey }: Props) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.list(topLevelFilterKey),
    refetchInterval: ONE_MINUTE,
    gcTime: getPollingGCTime(ONE_MINUTE),
    queryFn: async () => {
      const token = await getToken()
      const tasksApiClient = new TasksHttpClient({ authorization: token })

      const firstBatch = await fetchTasksBatch(tasksApiClient, 0, topLevelFilterKey)
      if (!firstBatch) return []
      const totalPages = Math.ceil(firstBatch.totalCount / tasksPerBatch)
      if (totalPages < 2) {
        return firstBatch.tasks
      }

      // All pages starting from index 1 (page 2)
      const pagesToLoad = range(1, totalPages)
      const restOfBatches = await Promise.all(pagesToLoad.map((page) => fetchTasksBatch(tasksApiClient, page, topLevelFilterKey)))
      const allTasks = [...firstBatch.tasks, ...restOfBatches.map((data) => data?.tasks).flat()].filter(Boolean) as Task[]

      // remove duplicates between batches just in case
      return uniqBy(allTasks, 'id')
    },
  })
}

const MY_TASK_COUNT_FILTER_KEY = 'pending'

export const useGetMyTaskCount = () => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: taskQueryKeys.listCount(MY_TASK_COUNT_FILTER_KEY),
    refetchInterval: ONE_MINUTE,
    gcTime: getPollingGCTime(ONE_MINUTE),
    queryFn: async () => {
      const token = await getToken()
      const tasksApiClient = new TasksHttpClient({ authorization: token })

      const { data } = await tasksApiClient.POST('/', {
        body: { paginationModel: { page: 0, pageSize: 0 }, topLevelFilter: MY_TASK_COUNT_FILTER_KEY },
      })

      return data?.totalCount ?? 0
    },
  })
}
